import { useState } from 'react';
import { Link } from 'react-router-dom';
import { intakeNoun } from '../intake/intakeNoun';
import { X } from 'lucide-react';
import { useAdvisorFirm } from './useAdvisorFirm';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { STEWARD_ROLES } from '../api/types';

const DISMISS_KEY = 'steward-app:advisorNudgeDismissed';

function readDismissed(): boolean {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}

/**
 * Post-login landing nudge (docs/25 §2, S7): tells an advisor their portal exists,
 * once, on the Dashboard they land on after signing in. Same local-dismiss pattern
 * as `NameNudge` — a permanent, no-nag choice remembered in this browser, not a
 * server-side preference (there is nothing here worth syncing across devices, and
 * every existing per-device dismiss in this app already works this way).
 *
 * Gated on `useAdvisorFirm()`, so read that hook's header before assuming this is
 * doing anything security-relevant: it is not. Nothing bad happens if this banner
 * showed to someone it shouldn't have (the link goes to a page that itself renders
 * "This area is for advisory-firm team members" for anyone `advisorFor` is null
 * for) or failed to show someone it should have (they can still type the URL). This
 * is purely "does the advisor know this exists", the same job NameNudge does for a
 * missing display name.
 *
 * ⚠️ **Hidden while the active membership itself carries `STEWARD_ROLES.ADVISOR`
 * (copy review, 2026-09-21).** `advisorFor` says the caller belongs to SOME advisory firm; it
 * says nothing about which workspace this Dashboard is currently rendering. An advisor who has
 * followed a client link is looking at that family's read-only workspace, and the family's own
 * membership there never carries this role, so its presence on the CURRENT membership is the
 * same signal `SharedDocuments.tsx`'s `readingAsAdvisor` uses to mean "this is a client's
 * workspace, viewed through an advisor connection". A card that says "Your advisor portal is
 * ready" over someone else's account is at best noise and at worst reads as if it belongs to
 * the family being viewed.
 */
export function AdvisorLandingNudge() {
  const { advisorFor } = useAdvisorFirm();
  const { activeMembership } = useWorkspace();
  // Seeded from storage once via the lazy initializer; nothing outside this
  // component's own `dismiss()` below ever changes the flag, so re-reading it on
  // every render (as NameNudge does, for a reason specific to it — see its own
  // comment) would just be a synchronous localStorage read this component has no
  // use for.
  const [dismissed, setDismissed] = useState(readDismissed);
  const viewingClientWorkspace = (activeMembership?.roleIds ?? []).includes(STEWARD_ROLES.ADVISOR);

  if (!advisorFor || dismissed || viewingClientWorkspace) return null;

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private browsing */ }
    setDismissed(true);
  }

  return (
    <div className="relative mb-8 rounded-token border border-bronze/30 bg-surface p-5 shadow-panel">
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-3 text-slate hover:text-navy bg-transparent border-none cursor-pointer p-1"
      >
        <X size={16} strokeWidth={1.75} aria-hidden />
      </button>
      <h2 className="font-serif text-[17px] font-normal m-0 mb-1 text-navy">
        Your advisor portal is ready.
      </h2>
      <p className="text-[13px] text-slate mb-4 max-w-md">
        As a member of {advisorFor.name}, you can see the clients who have connected
        with you and the {intakeNoun(advisorFor, 'plural')} they have sent in.
      </p>
      <Link
        to="/advisor"
        onClick={dismiss}
        className="inline-flex items-center gap-2 px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans no-underline"
      >
        Go to your portal
      </Link>
    </div>
  );
}
